'use client';

import { useObservable } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useProgress } from '@bprogress/next';
import { Link } from '@/components/Link';
import { cn } from '@/lib/cn';
import { setPreferredSiteCode } from '@/lib/preferredSite';
import analytics from '@/services/analytics';

export interface SiteSelectorProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    siteSlug?: string;
    localeSlug?: string;
}

export function SiteSelector({ siteSlug, localeSlug = 'en', className, ...props }: SiteSelectorProps) {
    const alternateSiteSlug = siteSlug === 'au' ? 'nz' : 'au';
    const alternateSiteName = siteSlug === 'au' ? 'New Zealand' : 'Australia';
    const isChecked = useObservable(siteSlug === 'au');
    const link = `/${alternateSiteSlug}/${localeSlug}`;
    const { start } = useProgress();
    const label = `Go to our ${alternateSiteName} site`;
    return (
        <div className={cn(className, 'flex gap-2 items-center')} {...props}>
            <span className="text-xs font-semibold">NZ</span>
            <Link
                href={link}
                onNavigate={() => {
                    isChecked.set((v) => !v);
                    analytics.plugins.interactions.trackInteraction({
                        source: 'jucy-website',
                        category: 'region-select',
                        action: 'click',
                        label: label,
                    });
                    start();
                    setPreferredSiteCode(alternateSiteSlug);
                }}
                aria-label={label}
            >
                <$React.div
                    name="region-selector"
                    $className={() =>
                        cn('toggle border-white !bg-transparent !text-white grid-cols-[0fr_1fr_1fr]', {
                            'grid-cols-[0fr_1fr_1fr]': !isChecked.get(),
                            'grid-cols-[1fr_1fr_0fr]': isChecked.get(),
                        })
                    }
                />
            </Link>
            <span className="text-xs font-semibold">AU</span>
        </div>
    );
}
